import { createContext, forwardRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInputWithCountrySelect, { type Value } from 'react-phone-number-input';
import ca from 'react-phone-number-input/locale/ca.json';
import en from 'react-phone-number-input/locale/en.json';
import es from 'react-phone-number-input/locale/es.json';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { Input as InputComponent } from '../Input';
import { type InputProps } from '../Input/types';
import { Label } from '../Label';
import { CountrySelect } from './components/CountrySelect';
import { FlagComponent } from './components/FlagComponent';
import { PhoneInputController } from './controller';
import { type PhoneInputVariant, type PhoneInputProps } from './types';

const PhoneInputContext = createContext<PhoneInputVariant>(undefined);

const Input = ({ className, type, ...props }: InputProps): React.ReactElement => {
  const variant = useContext(PhoneInputContext);
  return (
    <InputComponent
      className={cn('rounded-s-none rounded-e-lg', className)}
      type={type}
      variant={variant}
      {...props}
    />
  );
};

const PhoneInput = forwardRef<
  React.ComponentRef<typeof PhoneInputWithCountrySelect>,
  PhoneInputProps
>(
  (
    {
      className,
      onChange,
      value,
      label,
      required,
      helperText,
      variant,
      readOnly = false,
      defaultCountry = 'ES',
      ...props
    },
    ref
  ) => {
    const isHelperTextVisible = !!helperText?.trim();
    const { i18n } = useTranslation(LOCIZE_NS.COMMON);
    const getLabels = (): Record<string, string> => {
      const labels = {
        es,
        ca,
        en,
      };
      return labels[i18n.language as keyof typeof labels];
    };

    const handleChange = (phoneValue?: Value): void => {
      if (readOnly) return;
      onChange?.(phoneValue || ('' as Value));
    };

    return (
      <PhoneInputContext.Provider value={variant}>
        <div className="flex flex-col gap-2 w-full group/form" data-variant={variant}>
          {label && <Label required={required}>{label}</Label>}
          <PhoneInputWithCountrySelect
            ref={ref}
            className={cn('flex', className)}
            flagComponent={FlagComponent}
            countrySelectComponent={CountrySelect}
            inputComponent={Input}
            countrySelectProps={{ variant, readOnly }}
            labels={getLabels()}
            defaultCountry={defaultCountry}
            smartCaret={false}
            value={value || undefined}
            onChange={handleChange}
            readOnly={readOnly}
            {...props}
          />
          {isHelperTextVisible && (
            <span className="text-xs font-['Inter'] text-gray-500 font-light leading-4.5 min-h-4.5 group-data-[variant=invalid]/form:text-red-500">
              {helperText}
            </span>
          )}
        </div>
      </PhoneInputContext.Provider>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export { PhoneInputController, PhoneInput };
